import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Chip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const logStatusColor = (status) => {
	if (status === "success") return "success";
	if (status === "failed") return "error";
	return "default";
};

const EventLogsTab = ({ eventTypes, fetchEventLogs, emitEvent, isSubmitting }) => {
	const [logs, setLogs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [eventFilter, setEventFilter] = useState("");
	const [emitForm, setEmitForm] = useState({
		event_type: "tenant_rent_collected",
		payloadJson: '{\n  "payment_id": 0\n}',
	});

	const load = async () => {
		setLoading(true);
		setError("");
		const filters = {};
		if (eventFilter) filters.event_type = eventFilter;
		const result = await fetchEventLogs(filters);
		if (result.success) setLogs(result.logs);
		else setError(result.message);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [eventFilter]);

	const handleEmit = async () => {
		let payload;
		try {
			payload = JSON.parse(emitForm.payloadJson);
		} catch {
			setMessage("Payload must be valid JSON.");
			return;
		}
		setMessage("");
		const result = await emitEvent(emitForm.event_type, payload);
		if (result.success) {
			const data = result.data;
			setMessage(
				data?.journal_entry_id
					? `Event posted — journal entry #${data.journal_entry_id} (${data.status})`
					: `Event ${data?.status || "processed"} — check logs if no journal entry.`
			);
			load();
		} else {
			setMessage(result.message);
		}
	};

	return (
		<Box>
			<Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
				<FormControl size="small" sx={{ minWidth: 220 }}>
					<InputLabel>Filter by event</InputLabel>
					<Select label="Filter by event" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}>
						<MenuItem value="">All events</MenuItem>
						{eventTypes.map((et) => (
							<MenuItem key={et.event_type} value={et.event_type}>{et.label || et.event_type}</MenuItem>
						))}
					</Select>
				</FormControl>
				<Button variant="outlined" onClick={load}>Refresh</Button>
			</Box>

			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Typography variant="subtitle1" gutterBottom>Emit event (testing / replay)</Typography>
				<Box display="flex" flexDirection="column" gap={2}>
					<FormControl size="small" sx={{ maxWidth: 320 }}>
						<InputLabel>Event type</InputLabel>
						<Select
							label="Event type"
							value={emitForm.event_type}
							onChange={(e) => setEmitForm((f) => ({ ...f, event_type: e.target.value }))}
						>
							{eventTypes.map((et) => (
								<MenuItem key={et.event_type} value={et.event_type}>{et.label || et.event_type}</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="Payload (JSON)"
						value={emitForm.payloadJson}
						onChange={(e) => setEmitForm((f) => ({ ...f, payloadJson: e.target.value }))}
						multiline
						rows={4}
						fullWidth
						inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
					/>
					<Button
						variant="contained"
						startIcon={<PlayArrowIcon />}
						onClick={handleEmit}
						disabled={isSubmitting}
						sx={{ alignSelf: "flex-start" }}
					>
						{isSubmitting ? "Emitting…" : "Emit event"}
					</Button>
				</Box>
			</Paper>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			{message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage("")}>{message}</Alert>}

			{loading ? (
				<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
			) : (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Time</TableCell>
								<TableCell>Event</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Message</TableCell>
								<TableCell>Journal entry</TableCell>
								<TableCell>Payload</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{logs.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} align="center">No event logs.</TableCell>
								</TableRow>
							) : (
								logs.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell>{row.date_created ? new Date(row.date_created).toLocaleString() : "—"}</TableCell>
										<TableCell sx={{ fontFamily: "monospace" }}>{row.event_type}</TableCell>
										<TableCell>
											<Chip size="small" label={row.status} color={logStatusColor(row.status)} />
										</TableCell>
										<TableCell>{row.message || "—"}</TableCell>
										<TableCell>{row.related_journal_entry ?? "—"}</TableCell>
										<TableCell>
											<Typography variant="caption" sx={{ fontFamily: "monospace" }}>
												{JSON.stringify(row.payload)}
											</Typography>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	);
};

export default EventLogsTab;
